import React from "react";

import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { css } from "@emotion/react";
import cn from "classnames";

import { pipe } from "fp-ts/lib/function";
import { array } from "fp-ts";
import { ExpandedRichText, Link as LinkT } from "@swo/shared";
import { SwarovskiLink } from "../../components/_shared-components/ui-elements/SwarovskiLink";

export const renderTextOptions: Options["renderText"] = text => {
  const seed: Array<React.ReactNode> = [];

  const ret = pipe(text.split("\n"), removeConsecutiveDuplicates).reduce((children, textSegment, index) => {
    return index > 0 ? [...children, <br key={index} />, textSegment] : [...children, textSegment];
  }, seed);

  return ret;
};

function getValidHref(u: unknown): string {
  if (typeof u !== "string") {
    // Nothing we can do to fix it in a reasonable way.

    return u as string;
  }

  // Relative urls
  if (u.startsWith("/")) {
    return u;
  }

  if (isValidURL(u)) {
    return u;
  } else {
    const withHttps = `https://${u}`;

    if (isValidURL(withHttps)) {
      return withHttps;
    } else {
      return u;
    }
  }
}

function isValidURL(u: string): boolean {
  try {
    new URL(u);

    return true;
  } catch {
    return false;
  }
}

export const hyperlinksOptions: Options = {
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      const validHref = getValidHref(node.data.uri);

      return (
        <a href={validHref} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const link = node.data as LinkT;

      return link.type === "Internal Link" && !link.openInNewTab ? (
        <SwarovskiLink link={link.data} isInternal={true} scrollToTop={true}>
          {children}
        </SwarovskiLink>
      ) : (
        <a
          href={link.link}
          target={link.openInNewTab ? "_blank" : undefined}
          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      );
    },
  },
};

export const textOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return (
        <p
          className={node.data?.className}
          css={css`
            margin-top: 0;
          `}
        >
          {children}
        </p>
      );
    },
  },
  renderText: renderTextOptions,
};

export const defaultRichTextOptions: Options = {
  ...textOptions,
  renderNode: { ...textOptions.renderNode, ...hyperlinksOptions.renderNode },
};

export function transformJson(richTextDocument: ExpandedRichText): ExpandedRichText {
  if (richTextDocument?.json.nodeType === "document") {
    const transformedContent = richTextDocument.json.content
      .filter(c => {
        return !(
          c.nodeType === "paragraph" &&
          c.content &&
          c.content.length == 1 &&
          c.content[0]?.nodeType === "text" &&
          c.content[0]?.value === ""
        );
      })
      .map((c, idx, arr) => {
        if (idx === 0 || idx === arr.length - 1) {
          return {
            ...c,
            data: {
              ...c.data,
              className: cn({ "first-rt-node": idx === 0, "last-rt-node": idx === arr.length - 1 }),
            },
          };
        } else {
          return c;
        }
      });

    return {
      ...richTextDocument,
      json: {
        ...richTextDocument.json,
        nodeType: BLOCKS.DOCUMENT,
        content: transformedContent,
      },
    };
  } else {
    return richTextDocument;
  }
}

function removeTrailingLineBreaks(elements: Array<string>): Array<string> {
  const { rest: reversedElements } = pipe(
    [...elements].reverse(),
    array.spanLeft(el => el === "\n"),
  );

  return reversedElements.reverse();
}

function removeEmpty(elements: Array<string>): Array<string> {
  return elements.filter(el => el !== "");
}

function removeConsecutiveDuplicates(elements: Array<string>): Array<string> {
  const ret: Array<string> = [];

  elements.forEach(next => {
    if (ret.slice(-1)[0] === next) {
      return;
    } else {
      return ret.push(next);
    }
  });

  return ret;
}
